import React, { useState } from 'react';
import { router, usePage } from '@inertiajs/react';

export default function Index() {
    const { formulas, flash = {} } = usePage().props;
    const [openMenuId, setOpenMenuId] = useState(null);

    const handleDelete = (id, title) => {
        if (confirm(`Delete "${title || 'this brainstorm'}"? This can't be undone.`)) {
            router.delete(`/dashboard/formulas/${id}`);
        }
        setOpenMenuId(null);
    };

    const handleEdit = (uuid) => {
        router.visit(`/formulas/${uuid}/edit`);
    };

    const handleBuildFromScratch = () => {
        router.visit('/dashboard/formulas/create');
    };

    const handleImportFromJson = () => {
        router.visit('/formulas/import');
    };

    const handleExport = (formula) => {
        // Direct download — browser follows the export endpoint
        window.location.href = `/formulas/${formula.id}/export`;
        setOpenMenuId(null);
    };

    const handleSubmit = (formula) => {
        const isUpdate = !!formula.published_as_id;
        const msg = isUpdate
            ? 'Submit this update for an admin to apply to the public Brainstorm?'
            : 'Submit this brainstorm for an admin to review and possibly publish?';
        if (confirm(msg)) {
            router.post(`/dashboard/formulas/${formula.id}/submit`);
        }
    };

    const handleWithdraw = (formula) => {
        const isUpdate = !!formula.published_as_id;
        const msg = isUpdate
            ? "Withdraw this update? Your existing public Brainstorm is unaffected — only the pending update is cancelled."
            : 'Withdraw your submission?';
        if (confirm(msg)) {
            router.post(`/dashboard/formulas/${formula.id}/withdraw`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const truncate = (text, max = 140) => {
        if (!text) return '';
        return text.length > max ? text.substring(0, max).trim() + '…' : text;
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === id ? null : id);
    };

    // Close any open ⋯ menu on outside click
    React.useEffect(() => {
        if (!openMenuId) return;
        const close = () => setOpenMenuId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openMenuId]);

    /**
     * Compute the four-state model for a brainstorm:
     *   submitted_at | published_as_id | state
     *      null      |     null        | private  (no badge, action = Submit)
     *      set       |     null        | submitted        (action = Withdraw)
     *      null      |     set         | published        (action = Submit update)
     *      set       |     set         | update_pending   (action = Withdraw update)
     */
    const getStateInfo = (formula) => {
        const isSubmitted = !!formula.submitted_at;
        const isPublished = !!formula.published_as_id;
        if (!isSubmitted && !isPublished) {
            return {
                key: 'private',
                badge: null,
                actionLabel: 'Submit',
                actionVariant: 'btn-outline-secondary',
                actionIcon: 'bi-send',
                actionTooltip: 'Submit this brainstorm for an admin to review and possibly publish as a public Brainstorm.',
                onClick: handleSubmit,
            };
        }
        if (isSubmitted && !isPublished) {
            return {
                key: 'submitted',
                badge: { label: 'Submitted', className: 'bg-primary' },
                actionLabel: 'Withdraw',
                actionVariant: 'btn-outline-secondary',
                actionIcon: 'bi-x-circle',
                actionTooltip: 'Withdraw your submission. The admin will no longer see it in their queue.',
                onClick: handleWithdraw,
            };
        }
        if (!isSubmitted && isPublished) {
            return {
                key: 'published',
                badge: { label: 'Published', className: 'bg-success' },
                actionLabel: 'Submit update',
                actionVariant: 'btn-outline-secondary',
                actionIcon: 'bi-arrow-up-circle',
                actionTooltip: 'Submit your latest changes for an admin to apply to the existing public Brainstorm.',
                onClick: handleSubmit,
            };
        }
        return {
            key: 'update_pending',
            badge: { label: 'Update pending', className: 'bg-info' },
            actionLabel: 'Withdraw update',
            actionVariant: 'btn-outline-secondary',
            actionIcon: 'bi-x-circle',
            actionTooltip: 'Cancel the pending update. Your existing public Brainstorm is unaffected.',
            onClick: handleWithdraw,
        };
    };

    const hasFormulas = formulas && formulas.length > 0;

    return (
        <div className="container mt-4 mb-5">
            {/* Header */}
            <div className="mb-4">
                <h1 className="mb-1">My Brainstorms</h1>
                <p className="text-muted mb-0">Brainstorms you've created.</p>
            </div>

            {/* Flash messages */}
            {flash.success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {flash.success}
                    <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                </div>
            )}
            {flash.error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {flash.error}
                    <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                </div>
            )}

            {/* Top action buttons (only when the user already has at least one) */}
            {hasFormulas && (
                <div className="d-flex flex-wrap gap-2 mb-4">
                    <button onClick={handleBuildFromScratch} className="btn btn-primary">
                        <i className="bi bi-plus-circle me-2"></i>
                        Build from scratch
                    </button>
                    <button onClick={handleImportFromJson} className="btn btn-outline-primary">
                        <i className="bi bi-upload me-2"></i>
                        Import from JSON
                    </button>
                </div>
            )}

            {/* List or empty state */}
            {hasFormulas ? (
                <div className="row g-3">
                    {formulas.map(formula => {
                        const updated = formatDate(formula.updated_at || formula.created_at);
                        const isMenuOpen = openMenuId === formula.id;
                        const state = getStateInfo(formula);
                        return (
                            <div key={formula.id} className="col-12 col-md-6 col-lg-4">
                                <div className="card h-100 shadow-sm">
                                    <div className="card-body d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <h5 className="card-title mb-0 me-2">
                                                {formula.title || 'Untitled Brainstorm'}
                                            </h5>
                                            {state.badge && (
                                                <span className={`badge ${state.badge.className} flex-shrink-0`}>
                                                    {state.badge.label}
                                                </span>
                                            )}
                                        </div>
                                        {updated && (
                                            <div className="text-muted small mb-2">
                                                Updated {updated}
                                            </div>
                                        )}
                                        <p className="card-text text-secondary small flex-grow-1">
                                            {formula.description
                                                ? truncate(formula.description)
                                                : <em className="text-muted">No description yet.</em>}
                                        </p>

                                        <div className="d-flex align-items-center gap-2 mt-3">
                                            <button
                                                onClick={() => handleEdit(formula.uuid)}
                                                className="btn btn-sm btn-outline-primary"
                                            >
                                                <i className="bi bi-pencil me-1"></i> Edit
                                            </button>

                                            <button
                                                type="button"
                                                className={`btn btn-sm ${state.actionVariant}`}
                                                onClick={() => state.onClick(formula)}
                                                title={state.actionTooltip}
                                            >
                                                <i className={`bi ${state.actionIcon} me-1`}></i>
                                                {state.actionLabel}
                                            </button>

                                            <div className="ms-auto position-relative">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    aria-label="More actions"
                                                    aria-expanded={isMenuOpen}
                                                    onClick={(e) => toggleMenu(formula.id, e)}
                                                >
                                                    <i className="bi bi-three-dots"></i>
                                                </button>
                                                {isMenuOpen && (
                                                    <ul
                                                        className="dropdown-menu show shadow-sm"
                                                        style={{ position: 'absolute', right: 0, top: '100%', display: 'block' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => handleExport(formula)}
                                                            >
                                                                <i className="bi bi-download me-2"></i>
                                                                Export JSON
                                                            </button>
                                                        </li>
                                                        <li><hr className="dropdown-divider" /></li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item text-danger"
                                                                onClick={() => handleDelete(formula.id, formula.title)}
                                                            >
                                                                <i className="bi bi-trash me-2"></i>
                                                                Delete
                                                            </button>
                                                        </li>
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* Empty state with twin CTAs */
                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <div className="card h-100 border-primary">
                            <div className="card-body text-center p-4">
                                <i className="bi bi-stars text-primary" style={{ fontSize: '2rem' }}></i>
                                <h5 className="mt-2">Build from scratch</h5>
                                <p className="text-muted small mb-3">
                                    Start with a blank brainstorm and build it up step by step in the editor.
                                </p>
                                <button onClick={handleBuildFromScratch} className="btn btn-primary">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Build from scratch
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6">
                        <div className="card h-100">
                            <div className="card-body text-center p-4">
                                <i className="bi bi-upload text-secondary" style={{ fontSize: '2rem' }}></i>
                                <h5 className="mt-2">Import from JSON</h5>
                                <p className="text-muted small mb-3">
                                    Paste a JSON file generated from a brainstorm design brief and an LLM. The fastest way to a working brainstorm.
                                </p>
                                <button onClick={handleImportFromJson} className="btn btn-outline-primary">
                                    <i className="bi bi-upload me-2"></i>
                                    Import from JSON
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
